import React from "react";
import { Link } from "react-router-dom";
import LegalPageLayout from "./LegalPageLayout";
import C from "./legal.config";

export default function TermsOfService() {
  return (
    <LegalPageLayout title="Terms of Service" version={C.versions.terms}>
      <p>
        These Terms of Service govern your use of LEAGUE HUB
        ({C.site_url}), operated by <strong>{C.legal_entity_name}</strong>
        ({C.legal_address}). By creating an account, signing in, or
        otherwise using the service you agree to these terms.
      </p>

      <h2 className="legal-h2">1. The service</h2>
      <p>
        LEAGUE HUB is a tournament and ladder platform for amateur and
        semi-pro players of multiplayer video games. We host the
        infrastructure; <em>league managers</em> create and operate
        their own ladders and tournaments using our tools. Available
        tiers are documented on the <Link to="/" className="underline">pricing page</Link>:
        FREE Casual Profile, $1.99/mo PRO Player, FREE Casual League,
        $9.99/mo PRO League.
      </p>

      <h2 className="legal-h2">2. Eligibility &amp; accounts</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>You must be at least {C.minimum_age} years old to use LEAGUE HUB.</li>
        <li>You are responsible for activity on your account and for keeping your sign-in credentials safe.</li>
        <li>One natural person may not operate multiple accounts to evade bans, suspensions, or trust limits.</li>
        <li>We may suspend or terminate accounts that violate these terms or our <Link to="/legal/community" className="underline">Community Guidelines</Link>.</li>
      </ul>

      <h2 className="legal-h2">3. User-generated content</h2>
      <p>
        You retain ownership of content you submit (display name, avatar,
        banners, clips, screenshots, team logos, league branding, DMs,
        comments). By submitting content you grant us a worldwide,
        non-exclusive, royalty-free licence to host, store, display, and
        distribute that content as necessary to operate the service.
      </p>
      <p>You agree not to submit content that:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Infringes third-party intellectual property or publicity rights.</li>
        <li>Is illegal, defamatory, harassing, sexual content involving minors, or otherwise prohibited under Canadian law.</li>
        <li>Impersonates another person or misrepresents your identity.</li>
        <li>Contains malware, spam, or attempts to compromise the platform.</li>
      </ul>

      <h2 className="legal-h2">4. Prohibited conduct</h2>
      <p>Use of LEAGUE HUB is conditioned on you not:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Cheating, smurfing, account-sharing, or otherwise manipulating MMR/ratings.</li>
        <li>Scraping or extracting data at scale without our written permission.</li>
        <li>Reverse-engineering the platform or attempting to bypass authentication or rate limits.</li>
        <li>Using the platform to organise illegal activity, hate speech, or doxxing.</li>
        <li>Reselling subscriptions or PRO grants without our consent.</li>
      </ul>

      <h2 className="legal-h2">5. League managers — additional duties</h2>
      <p>
        PRO League holders operate their own ladders. As a league manager
        you are responsible for:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Setting fair, published rules for your ladder.</li>
        <li>Responding to disputes raised through the in-app moderation tools.</li>
        <li>Not publicly disclosing the personal information of your members beyond what they have already chosen to share.</li>
        <li>Complying with your local consumer-protection rules if you charge entry fees outside the platform.</li>
      </ul>
      <p>
        We provide moderation tools (REPORT PLAYER, dispute queue,
        inbox, ban controls) but the league manager is the first line
        of moderation for their own community.
      </p>

      <h2 className="legal-h2">6. Payments, subscriptions, refunds</h2>
      <p>
        Paid tiers renew automatically each month. Detailed billing
        terms — auto-renewal mechanics, price changes, cancellation
        path, and the consumer's right of cancellation in Canada — live
        on the <Link to="/legal/subscriptions" className="underline">Subscription Terms</Link>{" "}
        and <Link to="/legal/withdrawal" className="underline">Cancellation &amp; Refunds</Link> pages.
        Stripe is our payment processor.
      </p>

      <h2 className="legal-h2">7. Anti-cheat &amp; disputes</h2>
      <p>
        Match results, MMR, and badges are computed from data your
        client or league manager reports. We employ heuristic anti-cheat
        signals but we cannot guarantee that any particular match was
        played fairly. League managers and the central moderation team
        decide disputes; their decisions are final within the platform.
      </p>

      <h2 className="legal-h2">8. Disclaimers</h2>
      <p>
        LEAGUE HUB is provided "as is" and "as available". To the
        maximum extent permitted by law, {C.legal_entity_name}{" "}
        disclaims all implied warranties (merchantability, fitness for
        a particular purpose, non-infringement). We do <strong>not</strong>{" "}
        guarantee uninterrupted availability, error-free operation, or
        any particular competitive outcome.
      </p>
      <p>
        LEAGUE HUB is <strong>not affiliated with</strong> Riot Games,
        Valve, Electronic Arts, Epic Games, Activision Blizzard,
        Nintendo, or any other game publisher. Game titles are the
        trademarks of their respective owners; we reference them only
        to identify the games hosted on our ladders.
      </p>

      <h2 className="legal-h2">9. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by Canadian law,
        {" "}{C.legal_entity_name}'s aggregate liability for any claim
        arising out of or related to your use of LEAGUE HUB is limited
        to the greater of (a) the amount you paid us in the 12 months
        preceding the claim, or (b) CA$100. We are not liable for
        indirect, incidental, consequential, or punitive damages, loss
        of profits, or loss of data, even if we have been advised of
        the possibility of such damages.
      </p>
      <p>
        Nothing in these terms limits liability for fraud, fraudulent
        misrepresentation, death or personal injury caused by
        negligence, or any other liability that cannot be lawfully
        excluded.
      </p>

      <h2 className="legal-h2">10. Termination</h2>
      <p>
        You may close your account at any time from account settings or
        by emailing <a href={`mailto:${C.contact_email}`}>{C.contact_email}</a>.
        We may suspend or terminate accounts that violate these terms
        or our Community Guidelines; where lawful and proportionate we
        will give notice and an opportunity to remedy.
      </p>

      <h2 className="legal-h2">11. Governing law</h2>
      <p>
        These terms are governed by the laws of the Province of {C.province}{" "}
        and the federal laws of {C.country} applicable therein. The
        courts of {C.province} have exclusive jurisdiction over any
        dispute, except where consumer-protection law gives you the
        right to bring proceedings in your own jurisdiction.
      </p>

      <h2 className="legal-h2">12. Changes</h2>
      <p>
        We may update these terms from time to time. Material changes
        will be announced via in-app banner. Continued use of the
        platform after the effective date constitutes acceptance.
      </p>

      <p className="text-white/45 text-xs">
        Questions? Email <a href={`mailto:${C.contact_email}`}>{C.contact_email}</a>.
      </p>
    </LegalPageLayout>
  );
}
